import { Shield, Phone, Smartphone, MessageSquare, Clock, CheckCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const Services = () => {
  const { t } = useTranslation();
  
  const services = [
    {
      icon: Shield,
      title: t('services.items.fraudDetection.title'),
      description: t('services.items.fraudDetection.description'),
      features: t('services.items.fraudDetection.features', { returnObjects: true }) as string[]
    },
    {
      icon: Phone,
      title: t('services.items.hotline.title'),
      description: t('services.items.hotline.description'),
      features: t('services.items.hotline.features', { returnObjects: true }) as string[]
    },
    {
      icon: Smartphone,
      title: t('services.items.smartphoneSetup.title'),
      description: t('services.items.smartphoneSetup.description'),
      features: t('services.items.smartphoneSetup.features', { returnObjects: true }) as string[],
      link: "/services/smartphone-setup"
    },
    {
      icon: MessageSquare,
      title: t('services.items.dailySupport.title'),
      description: t('services.items.dailySupport.description'),
      features: t('services.items.dailySupport.features', { returnObjects: true }) as string[]
    }
  ];

  return (
    <section id="services" className="py-20 gradient-subtle">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl font-bold mb-4">
            {t('services.title')}
          </h2>
          <p className="text-xl text-muted-foreground">
            {t('services.description')}
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {services.map((service, index) => {
            const serviceUrls = {
              0: '/services/betrugs-erkennung',
              1: '/services/hotline', 
              2: '/services/smartphone-setup',
              3: '/services/alltagsunterstuetzung'
            };
            
            return (
              <Card key={index} className="shadow-soft hover:shadow-glow transition-all duration-300 group h-full flex flex-col">
                <CardHeader className="text-center">
                  <div className="mx-auto mb-4 w-16 h-16 gradient-primary rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <service.icon className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-xl">{service.title}</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col flex-1">
                  <p className="text-muted-foreground mb-4">{service.description}</p>
                  <ul className="space-y-2 mb-4">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-success flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-auto">
                    <Button 
                      asChild 
                      variant="outline" 
                      className="w-full"
                    >
                      <Link to={serviceUrls[index as keyof typeof serviceUrls]}>
                        {t('services.learnMore')}
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
        
        <div className="text-center">
          <Button 
            variant="hero" 
            size="xl" 
            className="shadow-glow"
            asChild
          >
            <Link to="/login">
              <Clock className="mr-2 h-5 w-5" />
              {t('services.cta')}
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Services;